/**
 * Streams the current user's active background project initialisations via
 * Server-Sent Events.  Source of truth lives in Redis (keyed by user id), so
 * the same user sees their jobs across devices and refreshes until the
 * worker marks the op complete and unregisters it.
 *
 * Falls back to a one-shot GET when SSE is unavailable (e.g. no token yet).
 */

import { useCallback, useEffect, useRef, useState } from "react";

import { getAuthToken } from "@/lib/getAuthToken";

const API_BASE = import.meta.env.VITE_API_URL || "";
const MY_INITS_URL = `${API_BASE}/api/my-initialisations`;
const MY_INITS_STREAM_URL = `${MY_INITS_URL}/stream`;
const RECONNECT_DELAY_MS = 5000;

export interface InitialisationProgress {
  step: number;
  totalSteps: number;
  stepLabel: string;
  detail: string;
  itemsDone: number;
  itemsTotal: number;
  complete: boolean;
  error: string | null;
}

export interface MyInitialisation {
  operationId: string;
  projectName: string;
  startedAt: string;
  /** Whether the job can be cancelled. Defaults true (project inits); the
   *  substitute-refresh job sets this false so no Cancel button is shown. */
  cancellable?: boolean;
  progress: InitialisationProgress | null;
}

interface Options {
  /** Skip streaming entirely (e.g. when user not authenticated). */
  enabled?: boolean;
  /** Called with operationId whenever a job transitions to complete. */
  onComplete?: (operationId: string, success: boolean) => void;
}

export function useMyInitialisations({ enabled = true, onComplete }: Options = {}) {
  const [items, setItems] = useState<MyInitialisation[]>([]);
  const seenCompleteRef = useRef<Set<string>>(new Set());
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  const applyItems = useCallback((data: MyInitialisation[]) => {
    setItems(data);
    for (const item of data) {
      if (item.progress?.complete && !seenCompleteRef.current.has(item.operationId)) {
        seenCompleteRef.current.add(item.operationId);
        onCompleteRef.current?.(item.operationId, !item.progress.error);
      }
    }
  }, []);

  const fetchOnce = useCallback(async () => {
    try {
      const token = await getAuthToken();
      const res = await fetch(MY_INITS_URL, {
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
      if (!res.ok) return;
      applyItems(await res.json());
    } catch {
      // ignore — stream will deliver the next update
    }
  }, [applyItems]);

  useEffect(() => {
    if (!enabled) return;

    let cancelled = false;
    let es: EventSource | null = null;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;

    const connect = async () => {
      if (cancelled) return;

      // Always refresh snapshot immediately — cheap and covers the case where
      // SSE is blocked by a proxy.
      await fetchOnce();
      if (cancelled) return;

      const token = await getAuthToken();
      const url = token
        ? `${MY_INITS_STREAM_URL}?token=${encodeURIComponent(token)}`
        : MY_INITS_STREAM_URL;

      es = new EventSource(url, { withCredentials: true });

      es.onmessage = (ev) => {
        try {
          applyItems(JSON.parse(ev.data) as MyInitialisation[]);
        } catch {
          // ignore malformed frame
        }
      };

      es.onerror = () => {
        // EventSource auto-reconnects, but if the server closed the stream
        // (e.g. token expired) we want a fresh connection with a new token.
        es?.close();
        es = null;
        if (cancelled) return;
        reconnectTimer = setTimeout(connect, RECONNECT_DELAY_MS);
      };
    };

    connect();

    return () => {
      cancelled = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      es?.close();
    };
  }, [enabled, applyItems, fetchOnce]);

  return { items, refetch: fetchOnce };
}
